import React, { useRef, useCallback, useState, useMemo } from 'react'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import TableLoader from 'components/TableLoader'
import { Tooltip, withStyles } from '@material-ui/core'
import { isEmpty } from 'lodash'

const HtmlTooltip = withStyles(theme => ({
  tooltip: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 300,
    maxHeight: 400,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip)

export const TableComponent = React.memo(({ columns, loading, data, rowStyle = {}, onRowClick = () => {}, selectedRowKey = '', enabledRowSelection, handlePageChange = () => {}, selectedRow = {}, setSelectedRow = () => {}, isForViewAction = false, hasFilters = false }) => {
  const cols = columns.length
  const [isFilterEnabled, setFilterEnabled] = useState(false)
  const observer = useRef(null)

  const handleRowClick = useCallback(
    row => {
      if (enabledRowSelection) setSelectedRow(row[selectedRowKey])
      onRowClick(row)
    },
    [enabledRowSelection, setSelectedRow, selectedRowKey, onRowClick]
  )

  const lastElementRef = useCallback(
    node => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) handlePageChange()
        },
        { threshold: 0.75 }
      )
      if (node) observer.current.observe(node)
    },
    [handlePageChange]
  )

  const handleHeaderClick = () => {
    if (!hasFilters) return
    setFilterEnabled(prev => !prev)
  }

  // Memoize table rows to prevent unnecessary re-renders
  const memoizedTableRows = useMemo(() => {
    if (!Array.isArray(data)) {
      console.error('Data is not an array:', data)
      return []
    }

    return data.map((row, index) => {
      const rowTooltip = typeof rowStyle === 'function' && rowStyle(row).backgroundColor === '#ffebeb' ? 'Duplicate QR code in this WO line' : ''

      return (
        <HtmlTooltip key={row.id || index} placement='top' title={rowTooltip} arrow>
          <TableRow style={typeof rowStyle === 'function' ? rowStyle(row) : rowStyle} onClick={() => handleRowClick(row)} ref={data.length === index + 1 ? lastElementRef : null} selected={!isEmpty(selectedRowKey) && selectedRow === row[selectedRowKey]} className={isForViewAction ? 'table-with-row-click' : ''} hover>
            {columns.map(col => (
              <TableCell style={{ fontSize: '12px', fontWeight: 400 }} key={col.accessor}>
                {col.render ? col.render(row) : row[col.accessor] || 'NA'}
              </TableCell>
            ))}
          </TableRow>
        </HtmlTooltip>
      )
    })
  }, [data ?? [], rowStyle, handleRowClick, lastElementRef, selectedRowKey, selectedRow, isForViewAction])

  return (
    <Table size='small' stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map(col => (
            <TableCell key={col.accessor} onClick={handleHeaderClick} className={hasFilters ? 'table-header-filter' : ''}>
              {col.name}
            </TableCell>
          ))}
        </TableRow>
        {isFilterEnabled && (
          <TableRow>
            {columns.map(col => (
              <TableCell key={col.accessor}>{col.filter ? col.filter() : ''}</TableCell>
            ))}
          </TableRow>
        )}
      </TableHead>

      {loading ? (
        <TableLoader cols={cols} />
      ) : isEmpty(data) ? (
        <TableBody>
          <TableRow>
            <TableCell colSpan={cols} className='Pendingtbl-no-datafound'>
              No data found
            </TableCell>
          </TableRow>
        </TableBody>
      ) : (
        <TableBody>{memoizedTableRows}</TableBody>
      )}
    </Table>
  )
})

export default TableComponent
